import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Transcript from './Transcript';
import {
  fetchChatHistory,
  fetchRunConversations,
  holdScenarioClock,
  releaseScenarioClock,
} from './api';
import './ScenarioTranscript.css';

/**
 * What was said in a run, read back over the scenario rather than instead of it. The face
 * underneath stays mounted, so the run, the session and the scene all live through this.
 *
 * Mounted only while it is up. That is what makes it safe: nothing here is remembered between two
 * openings, so it opens on its list every time, and the clock hold is taken and given back with
 * the mount rather than with a button nobody may press.
 *
 * @param runId the run whose conversations these are.
 * @param onClose what to do when the student is done reading.
 */
export default function ScenarioTranscript({ runId, onClose }) {
  const { t } = useTranslation();
  const [conversations, setConversations] = useState(null);
  // The conversation being read, or null while the list is up.
  const [reading, setReading] = useState(null);
  const [messages, setMessages] = useState(null);
  const [failed, setFailed] = useState(false);

  // Reading is not playing, so the run's clock stops for exactly as long as this is on the screen.
  // Hung off the mount rather than off the way out: a hold nobody releases stalls the run for
  // good, and a student who closes the tab mid-read has pressed nothing. Both calls are
  // idempotent, so React mounting an effect twice in development costs nothing.
  useEffect(() => {
    holdScenarioClock(runId).catch(() => setFailed(true));
    return () => {
      releaseScenarioClock(runId).catch(() => {});
    };
  }, [runId]);

  useEffect(() => {
    let cancelled = false;
    fetchRunConversations(runId)
      .then((found) => {
        if (!cancelled) setConversations(found);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [runId]);

  // A scene conversation is an ordinary chat, so this is the ordinary chat history — the same
  // turns, with the same annotations on them, drawn by the same transcript.
  useEffect(() => {
    if (!reading) {
      return undefined;
    }
    let cancelled = false;
    setMessages(null);
    fetchChatHistory(reading.chatId)
      .then((history) => {
        if (!cancelled) setMessages(history);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [reading]);

  const notice = (text) => <p className="player-transcript-notice">{text}</p>;

  const list = () => {
    if (conversations === null) {
      return notice(t('player.transcript.loading'));
    }
    if (conversations.length === 0) {
      return notice(t('player.transcript.empty'));
    }
    return (
      <ul className="player-transcript-list">
        {conversations.map((conversation) => (
          <li key={conversation.chatId}>
            <button type="button" onClick={() => setReading(conversation)}>
              {conversation.name}
            </button>
          </li>
        ))}
      </ul>
    );
  };

  const read = () => (
    <>
      <button
        type="button"
        className="player-transcript-back"
        onClick={() => setReading(null)}
      >
        ‹ {t('player.transcript.back')}
      </button>
      {messages ? (
        <div className="player-transcript-messages">
          <Transcript messages={messages} />
        </div>
      ) : (
        notice(t('player.transcript.loading'))
      )}
    </>
  );

  const body = () => {
    if (failed) {
      return notice(t('player.transcript.failed'));
    }
    return reading ? read() : list();
  };

  return (
    <div
      className="player-transcript"
      role="dialog"
      aria-labelledby="player-transcript-heading"
    >
      <header className="player-transcript-header">
        <h2 id="player-transcript-heading">{t('player.transcript.heading')}</h2>
        <button type="button" className="player-transcript-close" onClick={onClose}>
          {t('player.transcript.close')}
        </button>
      </header>
      {body()}
    </div>
  );
}
